<script lang="ts">
	import { onMount } from "svelte";
	import type { Order, OrderListParams } from "../../../../core/schema/Order";
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore";
	import listOrdersQuery from "../../appsync/queries/listOrders.graphql";
	import { requestAll } from "../../appsync/requestAll";
	import { getGlobalSettings } from "../../getGlobalSettings";
	import { getClients } from "../../appsync/getClients";
	import type { Image } from "../../../../core/schema/Image";
	import Preloader from "../Preloader.svelte";
	import Button from "../Button.svelte";
	import type { ProductForCustomer } from "../../../../core/schema/Product";
	import type { OrderProduct } from "../../../../core/schema/OrderProduct";
	import Modal from "../Modal.svelte";
	import { createSortByDate } from "../../../../core/utils/createSortByDate";
	import { cartPathMap } from "../../../../core/schema/paths/cartPathMap";
	import type { OrderVoucher } from "../../../../core/schema/OrderVoucher";
	import type { VoucherProduct } from "../../../../core/schema/VoucherProduct";
	import OrderCard from "./OrderCard.svelte";
	import { getCartStore } from "../../contexts/cartStoreContextKey.js";

	export let products: ProductForCustomer[];
	export let vouchers: VoucherProduct[];

	const cartStore = getCartStore();
	const globalSettings = getGlobalSettings();
	const { TENANT_LOCALE } = globalSettings;

	let orders: Order[] = [];

	const listOrders = createPromiseStore(async () => {
		const { graphQL } = await getClients(globalSettings);
		const orders = await requestAll<Order, OrderListParams>(graphQL, {
			query: listOrdersQuery,
		});
		return orders.toSorted(createSortByDate("createdAt", "desc"));
	});

	$: loading = $listOrders.status === "loading";

	let imageByProductId: Record<ProductForCustomer["id"], Image>;

	onMount(async () => {
		orders = await listOrders.call();
		imageByProductId = products.reduce((data: Record<ProductForCustomer["id"], Image>, product: ProductForCustomer) => {
			const { id } = product;
			const [image] = product.images;
			if (image) {
				data[id] = image;
			}
			return data;
		}, {});
	});

	let showWarningModal: Order | undefined;

	async function repeatOrder(order: Order): Promise<void> {
		cartStore.clear();
		await Promise.all([
			...order.products.map((product: OrderProduct) => {
				const productForCustomer = products.find(({ id }: ProductForCustomer) => product.product.id === id);
				if (productForCustomer === undefined) {
					throw new Error(`Product with ID ${product.product.id} not found.`);
				}
				return cartStore.addProduct(
					productForCustomer,
					product.quantity,
					productForCustomer.minimumOrderQuantity,
					product.options,
				);
			}),
			...order.vouchers.flatMap((voucher: OrderVoucher) => {
				const voucherProduct = vouchers.find(({ id }: VoucherProduct) => voucher.orderVoucherProduct.id === id);
				if (voucherProduct === undefined) {
					throw new Error(`Voucher with ID ${voucher.id} not found.`);
				}
				return Array(voucher.quantity)
					.fill(undefined)
					.map((_: undefined) => cartStore.addVoucher(voucherProduct));
			}),
		]);
		window.location.assign(`/${cartPathMap[TENANT_LOCALE]}`);
	}

	async function onRepeatOrder(order: Order): Promise<void> {
		if ($cartStore.value.order.products.length + $cartStore.value.order.vouchers.length) {
			showWarningModal = order;
		} else {
			await repeatOrder(order);
		}
	}

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<div class="w-full">
	<p class="text-md font-bold md:text-lg">Moje objednávky – {loading ? "" : ` ${orders.length}`}</p>

	{#if loading}
		<Preloader />
	{:else}
		<div class="mt-4 flex flex-col gap-6 md:mt-6">
			{#each orders as order}
				<OrderCard {order} {products} {imageByProductId} onRepeatOrder={() => onRepeatOrder(order)} />
			{:else}
				Nemáte žádné objednávky.
			{/each}
		</div>
	{/if}
</div>

<Modal show={showWarningModal !== undefined}>
	<p class="h2" slot="title">Košík není prázdný</p>
	<div class="flex flex-col items-center gap-2">
		<p>Zopakováním objednávky dojde k vyprázdnění vašeho košíku. Chcete pokračovat?</p>

		<Button
			on:click={() => {
				if (showWarningModal === undefined) {
					throw new Error("No order found.");
				} else {
					void repeatOrder(showWarningModal);
				}
			}}
		>
			Ano, vyprázdnit košík
		</Button>

		<Button
			variant="secondary"
			on:click={() => {
				showWarningModal = undefined;
			}}
		>
			Ne, nechat produkty v košíku
		</Button>
	</div>
</Modal>
